import { Check, Clock, AlertTriangle } from "lucide-react"

import { cn } from "@/lib/utils"
import { fa, type Cycle, type Step } from "../process"

const STEPS: Array<{ step: Step; title: string; who: string }> = [
  { step: "allocation", title: "ثبت نسبت کاری", who: "همه پرسنل" },
  { step: "payroll", title: "حقوق و هزینه‌ها", who: "مدیر مالی" },
  { step: "division", title: "تأیید مدیر واحد", who: "مدیران واحد" },
  { step: "unit", title: "تأیید نهایی کسب‌وکار", who: "مدیران کسب‌وکار" },
  { step: "close", title: "بستن دوره", who: "مدیر مالی" },
]

/**
 * The monthly close as five dated steps. A step is "past" once its deadline day has
 * gone by, "now" when it is the earliest step still open, and upcoming otherwise.
 * `highlight` marks the steps that belong to the viewer. When `doneSteps` is given
 * (close monitor), completion comes from verified counts instead of the calendar —
 * an overdue but unfinished step shows a warning, never a check.
 */
export function ProcessTimeline({ cycle, closed, highlight = [], doneSteps }: { cycle: Cycle; closed: boolean; highlight?: Step[]; doneSteps?: Step[] }) {
  const current = closed ? null : STEPS.find(({ step }) => !cycle.deadlines[step].passed)?.step ?? "close"
  return (
    <ol className="grid gap-2 sm:grid-cols-5" aria-label={`مراحل بستن ${fa(cycle.periodLabel)}`}>
      {STEPS.map(({ step, title, who }, index) => {
        const deadline = cycle.deadlines[step]
        const mine = highlight.includes(step)
        // A step the viewer still has open work in is never "done", whatever the calendar says.
        const done = doneSteps ? doneSteps.includes(step) || closed : closed || (deadline.passed && step !== current && !mine)
        const overdue = !done && deadline.passed
        const now = step === current
        return (
          <li
            key={step}
            aria-current={now ? "step" : undefined}
            className={cn(
              "relative flex gap-3 rounded-lg border p-3 sm:flex-col sm:gap-2",
              now && !done && "border-primary bg-primary/5 ring-1 ring-primary",
              done && "bg-muted/40",
              overdue && "border-loss/40 bg-loss/[0.03]",
            )}
          >
            <span
              className={cn(
                "grid size-7 shrink-0 place-items-center rounded-full border text-xs font-semibold",
                done ? "border-transparent bg-success text-white"
                  : overdue ? "border-transparent bg-loss/10 text-loss"
                  : now ? "border-primary bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground",
              )}
              aria-hidden
            >
              {done ? <Check className="size-4" /> : overdue ? <AlertTriangle className="size-4" /> : fa(index + 1)}
            </span>
            <div className="min-w-0">
              <p className="text-sm font-medium">{title}<span className="sr-only">{done ? " — انجام شد" : overdue ? " — دیرکرد" : ""}</span></p>
              <p className="text-xs text-muted-foreground">{who}</p>
              <p className={cn("mt-1 flex items-center gap-1 text-xs", overdue ? "font-medium text-loss" : "text-muted-foreground")}>
                {overdue ? <AlertTriangle className="size-3" aria-hidden /> : <Clock className="size-3" aria-hidden />}
                {step === "allocation" ? `۱ تا ${fa(deadline.day)}` : `تا ${fa(deadline.day)}`} {fa(deadline.label.split(" ").slice(1).join(" "))}
              </p>
              {mine && !done && <span className="mt-1.5 inline-block rounded-full bg-primary/10 px-2 py-px text-xs font-medium text-primary">کار شما</span>}
            </div>
          </li>
        )
      })}
    </ol>
  )
}
